import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { get, uniqBy } from 'lodash';

import GET_PROJECT_COLLABORATORS from '../gql/ProjectCollaborationQuery.graphql';
import {
  getCollaboratorsFromData,
  getCollaborationsFromData,
  getCurrentCompanyIdFromData,
  getHistoricalCollaborationsFromData,
  getProjectOwnerFromData,
} from 'components/projects/collaboration/helpers';

const useProjectCollaboration = (projectNodeId) => {
  const { data, loading, refetch } = useQuery(GET_PROJECT_COLLABORATORS, {
    variables: { id: projectNodeId },
    fetchPolicy: 'cache-and-network',
  });

  const collaborations = getCollaborationsFromData(data);
  const historicalCollaborations = getHistoricalCollaborationsFromData(data);

  const currentViewerCompanyId = getCurrentCompanyIdFromData(data);
  const owner = getProjectOwnerFromData(data);
  const totalCount = get(data, 'viewer.project.collaborations.totalCount', 0);

  const allCollaborations = useMemo(() => {
    const activeCollaboratorIds = new Set(
      collaborations.map((collaboration) => collaboration?.collaborator?.id)
    );

    const inactiveCollaborations = uniqBy(
      historicalCollaborations
        .filter((hc) => !activeCollaboratorIds.has(hc?.collaboratingCompany?.id))
        .map((collaboration) => ({
          ...collaboration,
          isActiveCollaboration: false,
        })),
      (item) => item.collaboratingCompany.id
    );

    const activeCollaborations = collaborations.map((collaboration) => ({
      ...collaboration,
      isActiveCollaboration: true,
    }));

    return [...activeCollaborations, ...inactiveCollaborations];
  }, [collaborations, historicalCollaborations]);

  return {
    allCollaborations,
    collaborations,
    collaborators: getCollaboratorsFromData(data),
    currentViewerCompanyId,
    loading,
    owner,
    owningCollaborator: getProjectOwnerFromData(data),
    refetch,
    totalCount,
  };
};

export default useProjectCollaboration;
